import { FC, useMemo } from "react";
import { RouteComponentProps } from "react-router-dom";
import { Alert, Box } from "@mui/material";
import { format } from "date-fns";
import __ from "src/utils/Translations";
import useBasePath from "src/utils/hooks/useBasePath";
import { Loader } from "src/Components/Loader/Loader";
import BreadcrumbsMui from "src/Components/BreadcrumbsMui/BreadcrumbsMui";
import {
  useFetchEmployee,
  useFetchAbsenceTypeBalance,
} from "src/Pages/Company/Absences/api/employee.queries";
import { useGetAbsenceType } from "src/Pages/Company/Absences/api/absenceTypes.queries";
import Title from "src/Pages/Company/Absences/Title/Title";
import ErrorDataFetching from "src/Components/Error/ErrorDataFetching";

import { BillingCycleConfigurationType } from "src/Pages/Company/Absences/AbsenceType/AbsenceType.models";
import EmployeeCard from "src/Pages/Company/Absences/EmployeeCard";
import AbsenceRecordsModifyBalanceForm from "./AbsenceRecordsModifyBalanceForm";

interface IMatchParams {
  employeeId: string;
  absenceTypeId: string;
}

const AbsenceRecordsModifyBalance: FC<RouteComponentProps<IMatchParams>> = ({
  match,
}) => {
  const { employeeId, absenceTypeId } = match.params;
  const basePath = useBasePath();

  const { data: employee, isError, isFetching } = useFetchEmployee(employeeId);

  const {
    data: absenceType,
    isFetching: isFetchingAbsenceType,
    isError: isErrorAbsenceType,
  } = useGetAbsenceType(absenceTypeId);

  const billingCycles = useMemo(() => {
    if (!absenceType) return [];

    const currentYear = new Date().getFullYear().toString();

    if (
      absenceType.billingCycleConfiguration?.type ===
      BillingCycleConfigurationType.Yearly
    ) {
      return [currentYear];
    }

    if (
      absenceType.billingCycleConfiguration?.type ===
      BillingCycleConfigurationType.Monthly
    ) {
      return [format(new Date(), "yyyy-MM")];
    }

    return [];
  }, [absenceType]);

  // Only needed to cache ETag
  const { isSuccess } = useFetchAbsenceTypeBalance(
    employeeId,
    billingCycles,
    absenceTypeId
  );

  if (isFetching || isFetchingAbsenceType) return <Loader />;

  if (!employee || !absenceType) return false;

  return (
    <>
      <BreadcrumbsMui
        items={[
          {
            label: __("Nieobecności"),
            to: `${basePath}/absences`,
          },
          {
            label: __("Kartoteki pracowników"),
            to: `${basePath}/absences/absence-records`,
          },
          {
            label: __("Zmień wymiar nieobecności"),
          },
        ]}
      />
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Title
          title={__("Zmień wymiar nieobecności")}
          subtitle={__("Dodaj lub odbierz dni na koncie pracownika.")}
        />
      </Box>
      {isError || isErrorAbsenceType ? (
        <ErrorDataFetching />
      ) : (
        <>
          <EmployeeCard data={employee} />
          {!absenceType.active && (
            <Alert severity="warning" sx={{ mb: "1.5rem" }}>
              {__("Wybrana nieobecność jest nieaktywna.")}
            </Alert>
          )}
          <AbsenceRecordsModifyBalanceForm
            employeeId={employeeId}
            dailyWorkingHours={employee.dailyWorkingHours}
            absenceType={absenceType}
          />
        </>
      )}
    </>
  );
};

export default AbsenceRecordsModifyBalance;
